import {
  Box,
  Skeleton,
  Alert,
  AlertIcon,
  Grid,
  GridItem,
  HStack,
  Spacer,
  Flex,
  Switch,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  useDisclosure,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { CdqResultItemGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  CollateralDataQualityCheckReportingDatesBadge,
  CollateralDataQualityCheckResultActionsMenu,
  CollateralDataQualityCheckResultBadge,
  LastCheckedBadge,
} from './CollateralDataQualityCheckComponents';
import { Typography } from 'packages/common-frontend/components/Typography';
import { useFetchCdqResultsQuery } from '~/core/routes/collateral-data-quality/cdq.gql.generated';
import { CollateralDataQualityCheckDetails } from '~/core/routes/collateral-data-quality/CollateralDataQualityCheckDetails';
import { ViewSqlDrawer } from '~/core/routes/collateral-data-quality/drawer/ViewSqlDrawer';
import { CDQCount } from '~/core/routes/collateral-data-quality/utils';
import { colors } from '~/packages/common-frontend/colors';

type ResultItem = {
  finleyReceivableId: string;
  currentReportingDate: Util.FullDateStr;
  previousReportingDate: Util.FullDateStr;
  [key: string]: Util.Primitive;
};

type AckResultItem = {
  finleyReceivableId: string;
  currentReportingDate: Util.FullDateStr;
  previousReportingDate: Util.FullDateStr;
  acknowledgementId: string | null;
  acknowledgedOn: string;
  acknowledgedBy: string;
  acknowledgedFor: Util.FullDateStr | null;
  [key: string]: Util.Primitive;
};

const parseUnAcknowledgedResultItem = (
  item: CdqResultItemGQLType,
): ResultItem => {
  const {
    finleyReceivableId,
    currentReportingDate,
    previousReportingDate,
    otherFields,
  } = item;

  const parsedOtherFields = JSON.parse(otherFields) as Record<
    string,
    Util.Primitive
  >;

  return {
    finleyReceivableId,
    currentReportingDate,
    previousReportingDate,
    ...parsedOtherFields,
  };
};

const parseAcknowledgedResultItem = (
  item: CdqResultItemGQLType,
): AckResultItem => {
  const { acknowledgementData, ...otherFields } = item;
  finleyAssert(acknowledgementData, 'acknowledgementData is null');
  const result = parseUnAcknowledgedResultItem(otherFields);

  return {
    ...result,
    acknowledgementId: acknowledgementData.acknowledgementId,
    acknowledgedBy: acknowledgementData.acknowledgedBy,
    acknowledgedOn: new Date(acknowledgementData.acknowledgedOn).toDateString(),
    acknowledgedFor: acknowledgementData.acknowledgedFor ?? null,
  };
};

export const CollateralDataQualityCheckResults = ({
  cdqConfigId,
  cdqConfigName,
  cdqConfigQuery,
  handleArchive,
  updateIssueCounts, // updates count after they are fetched.
  isDeactivateCdqConfigLoading,
  receivablesRoutePrefix,
}: {
  cdqConfigId: string;
  cdqConfigName: string;
  cdqConfigQuery: string;
  handleArchive: ({ configId }: { configId: string }) => Promise<void>;
  updateIssueCounts: (name: string, cdqCount: CDQCount) => void;
  isDeactivateCdqConfigLoading: boolean;
  receivablesRoutePrefix: string;
}) => {
  const showAcknowledgedTable = useDisclosure();
  const [lastAthenaQueryCompletedAt, setLastAthenaQueryCompletedAt] = useState<
    Date | undefined
  >();

  const {
    data: athenaQueryData,
    loading: isAthenaQueryLoading,
    error: athenaQueryError,
    refetch,
  } = useFetchCdqResultsQuery({
    variables: {
      cdqId: cdqConfigId,
    },
    skip: !cdqConfigId,
    onCompleted: (data) => {
      if (data && data.unacknowledged) {
        const currentTime = new Date();
        setLastAthenaQueryCompletedAt(currentTime);
      }
    },
  });

  const onRefetch = useCallback(() => {
    refetch();
    updateIssueCounts(cdqConfigId, {
      issuesAcknowledged: 0,
      issuesUnacknowledged: 0,
      loading: true,
    });
  }, [cdqConfigId, refetch, updateIssueCounts]);

  const onClickArchiveConfig = useCallback(async () => {
    await handleArchive({ configId: cdqConfigId });
  }, [handleArchive, cdqConfigId]);

  const cdqResults = useMemo(() => {
    if (!athenaQueryData) {
      return {
        numAcknowledged: 0,
        numUnacknowledged: 0,
        reportingDates: [],
        unacknowledgedItems: [],
        acknowledgedItems: [],
      };
    }

    const reportingDateSet = new Set<Util.FullDateStr>();
    const unacknowledgedItems: ResultItem[] = athenaQueryData.unacknowledged.Items.map(
      parseUnAcknowledgedResultItem,
    );
    const acknowledgedItems: AckResultItem[] = athenaQueryData.acknowledged.Items.map(
      parseAcknowledgedResultItem,
    );

    // for unacknowledged items capture the previous and current reporting dates.
    unacknowledgedItems.forEach((item) => {
      reportingDateSet.add(item.previousReportingDate);
      reportingDateSet.add(item.currentReportingDate);
    });

    const numAcknowledged =
      athenaQueryData.unacknowledged.totalAcknowledgedIssueCount;
    const numUnacknowledged =
      athenaQueryData.unacknowledged.totalUnAcknowledgedIssueCount;

    return {
      unacknowledgedItems,
      acknowledgedItems,
      numAcknowledged,
      numUnacknowledged,
      reportingDates: Array.from(reportingDateSet),
    };
  }, [athenaQueryData]);

  useEffect(() => {
    if (athenaQueryData && athenaQueryData.unacknowledged) {
      updateIssueCounts(cdqConfigId, {
        issuesAcknowledged: cdqResults.numAcknowledged,
        issuesUnacknowledged: cdqResults.numUnacknowledged,
        loading: false,
      });
    }
  }, [athenaQueryData, cdqResults, cdqConfigId, updateIssueCounts]);

  const viewSqlDrawer = useDisclosure();

  if (isAthenaQueryLoading || isDeactivateCdqConfigLoading) {
    return <Skeleton h="129px" />;
  }

  // if there is nothing to expand disable accordion.
  const disableAccordion =
    cdqResults.unacknowledgedItems.length === 0 &&
    cdqResults.acknowledgedItems.length === 0;

  return (
    <AccordionItem
      isDisabled={disableAccordion}
      borderColor={colors.gray[200]}
      borderRadius="8px"
      borderWidth="1px"
    >
      {({ isExpanded }) => (
        <Box p={isExpanded ? '24px 24px 0px 24px' : '24px'}>
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem colSpan={2}>
              <HStack mb="16px" alignItems="center" spacing="16px">
                <Typography variant="section-title" trim>
                  {cdqConfigName}
                </Typography>
                {/* Show badge for acknowledged and unacknowledged items count */}
                {!athenaQueryError && (
                  <CollateralDataQualityCheckResultBadge
                    numAcknowledged={cdqResults.numAcknowledged}
                    numUnacknowledged={cdqResults.numUnacknowledged}
                    mode="unacknowledged"
                  />
                )}
              </HStack>
              <CollateralDataQualityCheckReportingDatesBadge
                reportingDates={cdqResults.reportingDates}
              />
            </GridItem>
            <GridItem colSpan={1}>
              <HStack spacing="24px">
                <Spacer />
                {lastAthenaQueryCompletedAt && (
                  <LastCheckedBadge date={lastAthenaQueryCompletedAt} />
                )}
                <HStack spacing="4px">
                  {/* menu that renders actions like archive/view sql */}
                  <CollateralDataQualityCheckResultActionsMenu
                    onViewSQLQuery={viewSqlDrawer.onOpen}
                    onClickArchiveConfig={onClickArchiveConfig}
                    athenaQueryDataItems={cdqResults.unacknowledgedItems}
                    cdqConfigName={cdqConfigName}
                  />
                  <AccordionButton
                    disabled={!cdqResults.unacknowledgedItems.length}
                  >
                    <AccordionIcon />
                  </AccordionButton>
                </HStack>
              </HStack>
            </GridItem>
          </Grid>
          <AccordionPanel>
            {/* Show details for unacknowledged items */}
            {cdqResults.unacknowledgedItems.length !== 0 && (
              <CollateralDataQualityCheckDetails
                queryResults={cdqResults.unacknowledgedItems}
                mode="unacknowledged"
                receivablesRoutePrefix={receivablesRoutePrefix}
                cdqId={cdqConfigId}
                refetch={onRefetch}
              />
            )}

            {/* Show toggle switch if there are acknowledged items */}
            {cdqResults.acknowledgedItems.length !== 0 && (
              <Flex mt="10px" gap="0.75rem" alignItems="center">
                <Switch
                  size="sm"
                  isChecked={showAcknowledgedTable.isOpen}
                  onChange={showAcknowledgedTable.onToggle}
                />
                <Typography
                  inline
                  variant="detail-muted"
                  TextProps={{ flexShrink: 0 }}
                >
                  Show Acknowledged
                </Typography>
              </Flex>
            )}

            {showAcknowledgedTable.isOpen && (
              // Show details for acknowledged items when toggle is on
              <CollateralDataQualityCheckDetails
                queryResults={cdqResults.acknowledgedItems}
                mode="acknowledged"
                receivablesRoutePrefix={receivablesRoutePrefix}
                cdqId={cdqConfigId}
                refetch={onRefetch}
              />
            )}
          </AccordionPanel>

          {athenaQueryError && (
            <Alert status="error">
              <AlertIcon />
              {athenaQueryError.message}
            </Alert>
          )}

          {viewSqlDrawer.isOpen && (
            <ViewSqlDrawer
              onClose={viewSqlDrawer.onClose}
              sqlQuery={cdqConfigQuery}
              queryName={cdqConfigName}
            />
          )}
        </Box>
      )}
    </AccordionItem>
  );
};
